// app/src/lib/api.js — thin fetch wrapper for the Crew HTTP API.
//
// Every call injects the verified Cognito access token (Authorization: Bearer …)
// and, for workplace-scoped routes, the active workplaceId. The server is the
// source of truth (server-authoritative model); this is the only data path.

import { getConfig } from './config.js';
import { getAccessToken } from './auth/cognitoClient.js';

/**
 * Perform an authenticated API request.
 * @param {string} path   - e.g. "/me" or "/workplaces/{wid}/posts".
 * @param {{ method?:string, body?:any, workplaceId?:string, auth?:boolean }} [opts]
 * @returns {Promise<any>} parsed JSON (or null for 204).
 */
export async function api(path, opts = {}) {
  const cfg = getConfig();
  const { method = 'GET', body, workplaceId, auth = true } = opts;
  const headers = { 'content-type': 'application/json' };

  if (auth) {
    const token = await getAccessToken();
    if (!token) {
      const err = new Error('not signed in');
      err.code = 'UNAUTHENTICATED';
      throw err;
    }
    headers.authorization = `Bearer ${token}`;
  }
  // The active workplace travels as a header so the server can run requireMember.
  if (workplaceId) headers['x-crew-workplace'] = workplaceId;

  const res = await fetch(`${cfg.apiBaseUrl}${path}`, {
    method,
    headers,
    body: body == null ? undefined : JSON.stringify(body),
  });

  if (res.status === 204) return null;
  const text = await res.text();
  let json = null;
  try { json = text ? JSON.parse(text) : null; } catch { /* non-JSON (e.g. gateway/HTML error) */ }
  if (!res.ok) {
    const err = new Error((json && json.message) || `api ${res.status}`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json;
}
